// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient svg module decls must travel with these files: consumers (e.g. functions) compile ui-kit sources transitively without our tsconfig include
/// <reference path="./svg.d.ts" />
// packages/ui-kit/src/brand/WordmarkAnimato.tsx
// C3F v18 — reference: design/system/components/brand/WordmarkAnimato.{jsx,prompt.md}
//
// The animated C3F wordmark, sequence J: the word writes itself in cascade,
// then the AE butterfly lifts off the line and takes Spectrum. 2.45s total.
// BOUND TO ONBOARDING: the `once` key is the usage constraint, not an option —
// pass it and the sequence plays once per browser, after which the component
// renders the still wordmark (a repeated moment stops being a moment). The
// animation rules live in design/system/tokens/motion.css (.px-logo-*): an
// animated brand cannot be a static SVG asset, so the master is annotated at
// render time — there is no second copy of the paths that could diverge.
import { type CSSProperties, type HTMLAttributes, useEffect, useMemo, useState } from "react";
import wordmarkDarkRaw from "./assets/praesto-wordmark-dark.svg?raw";
import wordmarkLightRaw from "./assets/praesto-wordmark-light.svg?raw";
import wordmarkDarkUrl from "./assets/praesto-wordmark-dark.svg";
import wordmarkLightUrl from "./assets/praesto-wordmark-light.svg";

const SVG_NS = "http://www.w3.org/2000/svg";
const SEQUENCE_MS = 2450;

/**
 * Takes the master SVG source and returns a version annotated for the
 * animation: classes on the three AE glyph strokes, the gradient stops and the
 * letter groups. Nothing is redrawn — the paths stay the master's.
 */
export function prepareAnimatedWordmark(source: string): string | null {
  const doc = new DOMParser().parseFromString(source, "image/svg+xml");
  const svg = doc.documentElement;
  if (!svg || svg.nodeName === "parsererror") return null;

  svg.removeAttribute("width");
  svg.removeAttribute("height");

  svg.querySelectorAll("linearGradient stop").forEach((s, i) => {
    s.setAttribute("class", `px-logo-c${i + 1}`);
  });

  const letters = svg.querySelector("g[mask]");
  if (letters) {
    letters.setAttribute("class", "px-logo-letters");
    // Letters before and after the glyph, separated: they can yield its place
    // while it rises. Without a rule that moves them, the two groups are inert.
    const inX = (p: Element): number => {
      const m = /translate\(\s*([-\d.]+)/.exec(p.getAttribute("transform") ?? "");
      return m?.[1] !== undefined ? parseFloat(m[1]) : 0;
    };
    const letterPaths = Array.from(letters.querySelectorAll("path"));
    (
      [
        ["px-logo-before", false],
        ["px-logo-after", true],
      ] as const
    ).forEach(([cls, after]) => {
      const sel = letterPaths.filter((p) => inX(p) >= 377 === after);
      const first = sel[0];
      if (!first?.parentNode) return;
      const g = doc.createElementNS(SVG_NS, "g");
      g.setAttribute("class", cls);
      first.parentNode.insertBefore(g, first);
      sel.forEach((p) => g.appendChild(p));
    });
  }

  // The glyph strokes are the only paths with stroke-width: letters are fills.
  // Two identical triads — one inside the mask (the optical cut), one visible —
  // and they must move together, or the cut stays put while the glyph rises.
  [svg.querySelector("mask"), svg].forEach((root) => {
    if (!root) return;
    const strokes = Array.from(root.querySelectorAll("path[stroke-width]")).filter(
      (p) => root.nodeName === "mask" || p.parentNode === svg,
    );
    const first = strokes[0];
    if (strokes.length !== 3 || !first?.parentNode) return;
    const g = doc.createElementNS(SVG_NS, "g");
    g.setAttribute("class", "px-logo-rise");
    first.parentNode.insertBefore(g, first);
    strokes.forEach((p, i) => {
      p.setAttribute("pathLength", "1");
      p.setAttribute("class", i === 2 ? "px-logo-bar" : "px-logo-wing");
      g.appendChild(p);
    });
  });

  return new XMLSerializer().serializeToString(svg);
}

interface Props extends Omit<HTMLAttributes<HTMLElement>, "children"> {
  /** Wordmark ink variant. The onboarding welcome is an always-Night surface,
   *  so "light" (Paper ink) is the usual value there. */
  theme?: "light" | "dark";
  height?: number;
  /** Bump to replay (variant-comparison pages only). */
  replay?: number;
  /** The once-per-browser key (e.g. "onboarding"). Stored as px-logo-<key>. */
  once?: string;
  onDone?: () => void;
}

export function WordmarkAnimato({
  theme = "dark",
  height = 34,
  replay = 0,
  once,
  onDone,
  style,
  ...rest
}: Props) {
  if (height < 34) {
    console.warn(
      `[WordmarkAnimato] height ${height}px: sotto i 34px il logotipo non è leggibile. Usa AppIcon.`,
    );
  }
  const raw = theme === "light" ? wordmarkLightRaw : wordmarkDarkRaw;
  const stillUrl = theme === "light" ? wordmarkLightUrl : wordmarkDarkUrl;
  // Read once at mount: changing between renders would cut the sequence short.
  const [alreadySeen] = useState<boolean>(() => {
    if (!once) return false;
    try {
      return localStorage.getItem(`px-logo-${once}`) === "1";
    } catch {
      return false;
    }
  });
  const markup = useMemo(() => prepareAnimatedWordmark(raw), [raw]);

  useEffect(() => {
    if (!once || alreadySeen || markup === null) return;
    try {
      localStorage.setItem(`px-logo-${once}`, "1");
    } catch {
      /* private mode: the sequence simply replays next time */
    }
    if (!onDone) return;
    const t = setTimeout(onDone, SEQUENCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per mount
  }, []);

  // inline-block, not block: on a <span> width:auto would fill the container,
  // while Wordmark renders an <img> sized to its intrinsic box.
  const box: CSSProperties = { height, width: "auto", display: "inline-block", ...style };

  // If annotation fails the still brand renders — never nothing in the logo's
  // place. Same output when the sequence was already seen: it is the wordmark,
  // not a fallback.
  if (markup === null || alreadySeen) {
    return <img src={stillUrl} alt="Praesto" style={box} {...rest} />;
  }
  return (
    <span
      key={replay}
      role="img"
      aria-label="Praesto"
      className="px-logo-anim"
      style={{ ...box, "--logo-ink": theme === "light" ? "#F4F0E9" : "#100D16" } as CSSProperties}
      // Not a sink: the markup is our own brand SVG bundled at build time
      // (?raw import above) and annotated by prepareAnimatedWordmark — no
      // user-controlled content ever flows through here.
      dangerouslySetInnerHTML={{ __html: markup }}
      {...rest}
    />
  );
}
